import {
  findLanguageJourneyMetadata,
  getLanguageJourneyLaunchChapter,
  languageJourneyManifest,
  loadLanguageJourneyProgress,
} from "../languageJourneyClient";
import { LEGACY_SPANISH_PLAN_STORAGE_KEY, getSpanishPlanStorageKey } from "./spanish-plan-storage";
import { validateSpanishPlanMetadata } from "./spanish-plan-validation";
import { type SpanishJourneyPlan, SpanishJourneyPlanBaseSchema } from "./spanish-types";

function cachedSpanishPlan(userId: string | null): SpanishJourneyPlan | null {
  const metadata = findLanguageJourneyMetadata("es-ES");
  if (!metadata || typeof window === "undefined") return null;
  const keys = [getSpanishPlanStorageKey(userId)];
  if (!userId) keys.push(LEGACY_SPANISH_PLAN_STORAGE_KEY);
  for (const key of keys) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const plan = SpanishJourneyPlanBaseSchema.parse(JSON.parse(raw));
      return validateSpanishPlanMetadata(plan, languageJourneyManifest.version, {
        courseVersion: (metadata as { courseVersion?: number }).courseVersion,
        chapters: metadata.launchChapters.map((chapter) => ({
          id: chapter[0] as string,
          chapterNumber: chapter[4] as number,
        })),
      });
    } catch {
      /* A launch read never deletes or migrates a cache. */
    }
  }
  return null;
}

/** Preserve the existing next-chapter rules without waiting for display content. */
export function selectReadyJourneyLaunch({
  languageCode,
  chapterId,
  explicitChapterId,
  userId,
  subscribed,
}: {
  languageCode: string;
  chapterId: string | null;
  explicitChapterId: string | null;
  userId: string | null;
  subscribed: boolean;
}) {
  const metadata = findLanguageJourneyMetadata(languageCode);
  let chapter = getLanguageJourneyLaunchChapter(languageCode, chapterId);
  if (!metadata || !chapter) return { chapter: null, plan: null };
  const identities = metadata.launchChapters as Array<[string, boolean, string[], string, number]>;
  const progress = loadLanguageJourneyProgress(languageCode, userId);
  const completed = new Set(progress.completedChapterIds);
  const explicit = Boolean(chapterId) && explicitChapterId === chapterId;
  const isDefault = !chapterId || chapter.id === identities[0][0];
  const spanish = (metadata as { progressNamespace?: string }).progressNamespace === "spanish-v3";
  const plan = spanish ? cachedSpanishPlan(userId) : null;
  let recommendation: string | undefined;
  if (!explicit && spanish && isDefault && plan?.status === "active") {
    recommendation = plan.items.find((item) => !completed.has(item.chapterId))?.chapterId;
  } else if (!explicit && !spanish && (isDefault || completed.has(chapter.id))) {
    const next = identities.find(([id]) => !completed.has(id));
    if (next && (subscribed || next[1])) recommendation = next[0];
  }
  if (recommendation) chapter = getLanguageJourneyLaunchChapter(languageCode, recommendation)!;
  if (!subscribed && !chapter.isFreePreview) {
    const free = identities.find(([, preview]) => preview);
    chapter = free ? getLanguageJourneyLaunchChapter(languageCode, free[0])! : chapter;
  }
  return { chapter, plan };
}
